import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  Chip,
  Typography,
} from "@mui/material";
import React, { useEffect, useState, useMemo, useCallback } from "react";
import { useSelector } from "react-redux";
import { useDispatch } from "react-redux";

import DownArrow from "../../../../styles/svg/downArrow.svg";
import {
  addAllBoard,
  addBoard,
  removeBoard,
} from "../../../../redux/SearchSlice";
import { useCustomSearchParams } from "../../../../hooks/useCustomSearchParams";
import { useResponsive } from "../../../../hooks/useResponsive";
import { useMineCards } from "../../../../utils/reactQuery/cards";
import { useUserWorkflowList } from "../../../../utils/reactQuery/workflows";
import CustomMenu from "../../../../components/Menu";
import { CustomButton } from "../../../../components/Button/Button";
import BoardSearch from "./BoardSearch";
import BoardList from "./BoardList";
import SelectAllBoardMenuItem from "./SelectAllBoardMenuItem";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";

const BoardChip = React.memo(({ checkedList, onDelete }) => (
  <Chip
    label={
      <div className="d-flex justify-content-space-between">
        <p className="filter-name">board</p>
        {checkedList.length > 0 && (
          <p className="filter-count">{checkedList.length}</p>
        )}
      </div>
    }
    className="filter-chip"
    {...(checkedList.length > 0 ? { onDelete } : {})}
  />
));

const MemoizedBoardSearch = React.memo(({ onSearch, searchQuery }) => (
  <div onKeyDown={(e) => e.stopPropagation()}>
    <BoardSearch onSearch={onSearch} searchQuery={searchQuery} />
  </div>
));

const MemoizedBoardList = React.memo(({ boards, checkedList, onSelect }) => (
  <BoardList boards={boards} checkedList={checkedList} onSelect={onSelect} />
));

function Boards({ isAssignToMe = false }) {
  const [Oldboard, updateParams, removeSearchParam] =
    useCustomSearchParams("board");
  const [activeBoardList, setactiveBoardList] = useState([]);
  const [searchQuery, setSearchQuery] = useState("");
  const dispatch = useDispatch();
  const isMobile = useResponsive("down", "md");

  const activeBoard = useSelector((state) => state.activeBoard.activeBoardList);
  const checkedList = useSelector((state) => state.search.boardSearch);
  const user = useSelector((state) => state.search.myTaskUser);

  const { userWorkflows } = useUserWorkflowList();
  const genericWorkflowId = useMemo(
    () =>
      userWorkflows?.data?.data?.responseData?.find(
        (workflow) => workflow.is_generic
      )?.workflow_id,
    [userWorkflows]
  );

  const { data: mineCards, isLoading } = useMineCards(
    user || [],
    "",
    "",
    "",
    genericWorkflowId
  );

  const filteredItems = useMemo(() => {
    if (!activeBoardList) return [];

    return searchQuery
      ? [
          ...activeBoardList.filter((item) =>
            item.board_name.toLowerCase().includes(searchQuery.toLowerCase())
          ),
        ].sort(
          (a, b) =>
            checkedList.includes(b.board_id) - checkedList.includes(a.board_id)
        )
      : [...activeBoardList].sort(
          (a, b) =>
            checkedList.includes(b.board_id) - checkedList.includes(a.board_id)
        );
  }, [activeBoardList, searchQuery, checkedList]);

  const boardsWithCards = useMemo(() => {
    if (!mineCards?.data?.data?.responseData) return new Set();
    return new Set(
      mineCards.data.data.responseData.map((card) => card.card.board_id)
    );
  }, [mineCards]);

  useEffect(() => {
    if (isAssignToMe && activeBoard && mineCards) {
      const filteredBoards = activeBoard.filter((board) =>
        boardsWithCards.has(board.board_id)
      );
      setactiveBoardList(filteredBoards);
    } else {
      setactiveBoardList(activeBoard);
    }
  }, [activeBoard, isLoading]);

  const [anchorEl, setAnchorEl] = React.useState(null);
  const open = Boolean(anchorEl);

  const selectBoard = useCallback(
    (event, id) => {
      if (!checkedList.includes(id)) {
        dispatch(addBoard(id));
        !isMobile && updateParams("board", [...checkedList, id]);
      } else {
        !isMobile &&
          updateParams(
            "board",
            checkedList.filter((el) => el != id)
          );
        dispatch(removeBoard(id));
      }
      setSearchQuery("");
    },
    [checkedList, dispatch, updateParams]
  );

  const selectAll = useCallback(
    (e) => {
      if (activeBoardList.length != checkedList.length) {
        !isMobile &&
          updateParams(
            "board",
            activeBoardList.map((el) => el.board_id)
          );
        dispatch(addAllBoard(activeBoardList.map((el) => el.board_id)));
      } else {
        !isMobile && removeSearchParam("board");
        dispatch(addAllBoard([]));
      }
    },
    [activeBoardList, checkedList, dispatch, updateParams, removeSearchParam]
  );

  const handleDeleteBoard = useCallback(() => {
    removeSearchParam("board");
    dispatch(addAllBoard([]));
  }, [dispatch, removeSearchParam]);

  const handleClose = useCallback(() => {
    setAnchorEl(null);
    setSearchQuery("");
  }, []);

  const handleButtonClick = useCallback((event) => {
    setAnchorEl(event.currentTarget);
  }, []);

  useEffect(() => {
    if (Oldboard) {
      const board = Oldboard.split(",").map((el) => el * 1);
      board.length > 1
        ? dispatch(addAllBoard(board))
        : dispatch(addBoard(board[0]));
    }
  }, [Oldboard, dispatch]);

  const isAllSelected = useMemo(
    () => activeBoardList.length === checkedList.length,
    [activeBoardList.length, checkedList.length]
  );

  return (
    <div>
      {isMobile ? (
        <Accordion className="filter-accordion">
          <AccordionSummary
            expandIcon={<ExpandMoreIcon />}
            aria-controls="panel1-content"
            id="panel1-header">
            <Typography component="span">
              By Board
              <span className="filter-count">
                {checkedList.length === 0 ? "" : checkedList.length}
              </span>
            </Typography>
          </AccordionSummary>
          <AccordionDetails sx={{ padding: "0px 12px 12px" }}>
            <MemoizedBoardSearch
              onSearch={setSearchQuery}
              searchQuery={searchQuery}
            />
            <div className="filter-accordion-content">
              {!searchQuery && (
                <SelectAllBoardMenuItem
                  selectAll={selectAll}
                  checked={isAllSelected}
                  className="select-all-menu-item"
                />
              )}
              <MemoizedBoardList
                boards={filteredItems}
                checkedList={checkedList}
                onSelect={selectBoard}
              />
            </div>
          </AccordionDetails>
        </Accordion>
      ) : (
        <CustomButton
          variant="outlined"
          color="primary"
          id="epics-board-dropdown"
          aria-haspopup="true"
          onClick={handleButtonClick}
          endIcon={<img src={DownArrow} alt="down arrow" />}
          className="white-button ml-2 board-filter-button">
          <BoardChip checkedList={checkedList} onDelete={handleDeleteBoard} />
        </CustomButton>
      )}
      <CustomMenu
        id="user-menu"
        anchorEl={anchorEl}
        open={open}
        onClose={handleClose}
        className="assign-board-owner-list board-dropdown-menu">
        <MemoizedBoardSearch
          onSearch={setSearchQuery}
          searchQuery={searchQuery}
        />
        {!searchQuery && (
          <SelectAllBoardMenuItem
            selectAll={selectAll}
            checked={isAllSelected}
            className="select-all-menu-item-in-Allboards"
          />
        )}
        <MemoizedBoardList
          boards={filteredItems}
          checkedList={checkedList}
          onSelect={selectBoard}
        />
      </CustomMenu>
    </div>
  );
}

export default React.memo(Boards);
